import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { Card, Col, Row, Alert, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import StarRatingComponent from 'react-star-rating-component'
import { AppDispatch } from '../store'
import { fetchPlacement } from '../store/placementSlice'
import { RootState } from '../store/rootReducer'
import numeral from 'numeral'
import PlacementAttendanceModal from './PlacementAttendanceModal'

const Placement = (): React.ReactElement | null => {
  const { t } = useTranslation(['placement', 'common'])
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { placement, loading, error } = useSelector((state: RootState) => state.placement)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    dispatch(fetchPlacement(id))
  }, [dispatch, id])

  const handleCloseModal = (response: boolean) => {
    setShowModal(false)
    if (response) {
      dispatch(fetchPlacement(id))
    }
  }

  if (loading === 'pending') {
    // TODO: Loader...
    return null
  }

  if (error || !placement) {
    // TODO: Upp kom óvænt villa...
    return null
  }

  return (
    <>
      {placement.is_moved && (
        <Alert variant="warning">
          {t('faerdur')}
          {placement.date_to && (
            <>
              {' '}
              {t('common:fra')} {dayjs(placement.date_to).format('DD.MM.YYYY')}
            </>
          )}
          .
        </Alert>
      )}
      {placement.is_removed && (
        <Alert variant="warning">
          {t('fjarlaegdur')}
          {placement.date_to && (
            <>
              {' '}
              {t('common:fra')} {dayjs(placement.date_to).format('DD.MM.YYYY')}{' '}
            </>
          )}
          .
        </Alert>
      )}

      <Card>
        <Card.Header>
          <Row>
            <Col>
              <h4 className="m-0">{t('upplysingar')} </h4>
            </Col>
            {!placement.is_removed && placement.attendance_type_count > 0 && (
              <Col>
                {(dayjs().isBefore(dayjs(placement.placement_group.period_part?.date_to)) ||
                  dayjs().isBefore(dayjs(placement.placement_group.period.date_to)) ||
                  dayjs().isBefore(dayjs(placement.date_to))) && (
                  <Button
                    variant="link"
                    className="float-right cursor-pointer mt-2"
                    style={{ color: '#008ad0', fontSize: 14, fontWeight: 'lighter' }}
                    onClick={() => setShowModal(true)}
                  >
                    {t('tilkynnaforfoll')}
                  </Button>
                )}
              </Col>
            )}
          </Row>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col>
              <strong>{t('nafn')}</strong>
              <div>{placement.person.name}</div>
            </Col>
            <Col>
              <strong>{t('kennitala')}</strong>
              <div>{placement.person.ssn}</div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <strong>{t('hopur')}</strong>
              <div>{placement.placement_group.name}</div>
            </Col>
            <Col>
              <strong>{t('vinnustadur')}</strong>
              <div>{placement.placement_group.workplace.name}</div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <strong>{t('timabil')}</strong>
              <div>{placement.placement_group.period.name}</div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <strong>{t('dagsFra')}</strong>
              <div>
                {placement.date_from ? (
                  <span>{dayjs(placement.date_from).format('DD.MM.YYYY')}</span>
                ) : placement.placement_group.period_part ? (
                  <span>{dayjs(placement.placement_group.period_part.date_from).format('DD.MM.YYYY')}</span>
                ) : (
                  <span>{dayjs(placement.placement_group.period.date_from).format('DD.MM.YYYY')}</span>
                )}
              </div>
            </Col>
            <Col>
              <strong>{t('dagsTil')}</strong>
              <div>
                {placement.date_to ? (
                  <span>{dayjs(placement.date_to).format('DD.MM.YYYY')}</span>
                ) : placement.placement_group.period_part ? (
                  <span>{dayjs(placement.placement_group.period_part.date_to).format('DD.MM.YYYY')}</span>
                ) : (
                  <span>{dayjs(placement.placement_group.period.date_to).format('DD.MM.YYYY')}</span>
                )}
              </div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <strong>{t('bankareikningur')}</strong>
              <div>{placement.account || '-'}</div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <strong>{t('placementGoupHours')}</strong>
              <div>
                {placement.placement_group_hours_total
                  ? numeral(placement.placement_group_hours_total).format('0,0.[00]')
                  : 0}
              </div>
            </Col>
            <Col>
              <strong>{t('periodHours')}</strong>
              <div>
                {placement.period_hours_total ? numeral(placement.period_hours_total).format('0,0.[00]') : 0}
                {placement.period_max_hours ? <>/ {numeral(placement.period_max_hours).format('0,0.[00]')}</> : ''}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <br />
      <Card>
        <Card.Header>
          <h4 className="m-0">{t('leidbeinendur')}</h4>
        </Card.Header>
        <Card.Body>
          {placement.instructors.length === 0 ? (
            <i>{t('engirLeidbeinendur')}</i>
          ) : (
            <table className="table white-table">
              <thead>
                <tr>
                  <th className="border-top-0">{t('nafn')}</th>
                </tr>
              </thead>
              <tbody>
                {placement.instructors.map((instructor, idx) => (
                  <tr key={`instructor-${idx}`}>
                    <td>{instructor.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>
      <br />
      <Card>
        <Card.Header>
          <h4 className="m-0">{t('mataframmistodu')}</h4>
        </Card.Header>
        <Card.Body>
          {placement.review_questions.length > 0 &&
            placement.review_questions.map((r, i) => (
              <Row>
                <Col>
                  <div>
                    <strong>{r.review_question.question}</strong>
                  </div>
                  <StarRatingComponent name={`rating-${i}`} value={r.rating} editing={false} />
                </Col>
              </Row>
            ))}
          <Row>
            <Col>{placement.review && placement.review.length > 0 ? placement.review : <i>{t('ekkiSkrad')}</i>}</Col>
          </Row>
        </Card.Body>
      </Card>
      <br />
      <Card>
        <Card.Header>
          <h4 className="m-0">{t('vidvera')}</h4>
        </Card.Header>
        <Card.Body>
          {placement.attendances.length === 0 ? (
            <i>{t('enginVidvera')}</i>
          ) : (
            <table className="table white-table">
              <thead>
                <tr>
                  <th className="border-top-0">{t('dags')}</th>
                  <th className="border-top-0">{t('fjTima')}</th>
                  <th className="border-top-0">{t('athugasemd')}</th>
                  <th className="border-top-0">{t('skradAf')}</th>
                </tr>
              </thead>
              <tbody>
                {placement.attendances.map((attendance) => (
                  <tr key={attendance.id}>
                    <td>{dayjs(attendance.date).format('DD.MM.YYYY')} </td>
                    <td>{attendance.attendance_type ? attendance.attendance_type.name : attendance.hours}</td>
                    <td>{attendance.comment}</td>
                    <td>{attendance.created_by_registration ? attendance.created_by_registration.name : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>
      {showModal && (
        <PlacementAttendanceModal
          id={id}
          placementDateFrom={
            placement.date_from
              ? placement.date_from
              : placement.placement_group.period_part
              ? placement.placement_group.period_part.date_from
              : placement.placement_group.period.date_from
          }
          placementDateTo={
            placement.date_to
              ? placement.date_to
              : placement.placement_group.period_part
              ? placement.placement_group.period_part.date_to
              : placement.placement_group.period.date_to
          }
          onClose={handleCloseModal}
        />
      )}
    </>
  )
}

export default Placement
